import React from 'react';
import { Chip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import TableComponent, { Data, HeadCell } from '../../component/table/table.component';
import {
    isOcrAuditFieldModified,
    resolveOcrAuditFieldLabel,
    resolveOcrConfidenceLevel,
} from '../../component/archive-form/anagrafica-ocr';
import { dateFormat } from '../../constants/utils';
/** 复用建档 OCR 置信度三档标签样式。 */
import '../../component/archive-form/archive-form.scss';

/** 字段表一行（会话 fields[] 或 ocr_audit 展平行）。 */
export type AnagraficaOcrFieldTableRow = {
    /** 实体字段名。 */
    field_id: string;
    /** 识别原值。 */
    value_ocr?: string;
    /** 确认值。 */
    value_confirmed?: string;
    /** 置信度 0–1。 */
    confidence?: number;
    /** 后端分档；缺失时按阈值本地计算。 */
    confidence_level?: string;
    /** 采纳侧。 */
    chosen?: string | null;
    /** 核对人。 */
    confirmed_by?: string;
    /** 核对时间。 */
    confirmed_at?: string;
};

/**
 * 将表格 Data 断言为 OCR 字段行。
 * @param row TableComponent 行
 */
function asFieldRow(row: Data): AnagraficaOcrFieldTableRow {
    return row as AnagraficaOcrFieldTableRow;
}

/**
 * OCR 字段只读表：识别原值 / 确认值 /「已修改」标记。
 * 会话详情与餐厅 ocr_audit 弹窗共用。
 */
export default function AnagraficaOcrFieldTable(props: {
    /** 表格行。 */
    rows: AnagraficaOcrFieldTableRow[];
    /** 表格 loading。 */
    isTableLoading?: boolean;
}) {
    useTranslation();
    const { rows, isTableLoading = false } = props;

    const headCellList: HeadCell[] = [
        {
            id: 'field_id',
            label: i18n.t('anagrafica_ocr_col_field'),
            renderFunction: (row) => (
                <Typography variant="body2">
                    {resolveOcrAuditFieldLabel(asFieldRow(row).field_id, (key) => i18n.t(key))}
                </Typography>
            ),
        },
        {
            id: 'value_ocr',
            label: i18n.t('anagrafica_ocr_col_value_ocr'),
            renderFunction: (row) => (
                <Typography variant="body2">{asFieldRow(row).value_ocr || '—'}</Typography>
            ),
        },
        {
            id: 'value_confirmed',
            label: i18n.t('anagrafica_ocr_col_value_confirmed'),
            renderFunction: (row) => (
                <Typography variant="body2">{asFieldRow(row).value_confirmed || '—'}</Typography>
            ),
        },
        {
            id: 'modified',
            label: i18n.t('anagrafica_ocr_col_modified'),
            renderFunction: (row) => (
                isOcrAuditFieldModified(asFieldRow(row))
                    ? <Chip size="small" color="warning" label={i18n.t('anagrafica_ocr_tag_modified')} />
                    : <Chip size="small" color="default" label={i18n.t('anagrafica_ocr_tag_unmodified')} />
            ),
        },
        {
            id: 'confidence',
            label: i18n.t('anagrafica_ocr_col_confidence'),
            renderFunction: (row) => {
                const field = asFieldRow(row);
                if (field.confidence == null || Number.isNaN(Number(field.confidence))) {
                    return <Typography variant="body2">—</Typography>;
                }
                /** 置信度数值 0–1。 */
                const confidence = Number(field.confidence);
                /** 百分比整数，与 ArchiveOcrFieldFrame 一致。 */
                const percent = Math.round(confidence * 100);
                /** 高/中/低三档，优先后端 confidence_level。 */
                const level = resolveOcrConfidenceLevel(confidence, field.confidence_level);
                return (
                    <Typography
                        component="span"
                        variant="caption"
                        className={`archive-ocr-field__confidence archive-ocr-field__confidence--${level}`}
                        data-testid={`archive-ocr-confidence-${level}`}
                    >
                        {i18n.t(`archive_form_ocr_level_${level}`, { percent })}
                    </Typography>
                );
            },
        },
        {
            id: 'confirmed_at',
            label: i18n.t('anagrafica_ocr_col_confirmed_at'),
            renderFunction: (row) => {
                const field = asFieldRow(row);
                return (
                    <Typography variant="body2">
                        {field.confirmed_at
                            ? dateFormat(field.confirmed_at, 'YYYY/MM/DD HH:mm:ss')
                            : '—'}
                    </Typography>
                );
            },
        },
    ];

    return (
        <TableComponent
            showChecked={0}
            list={rows}
            headCellList={headCellList}
            isTableLoading={isTableLoading}
            isPagination={false}
            hasExpand={false}
        />
    );
}
